import { Briefcase02, Users02 } from '@timelyapp/icons/solid-icons';
import { Box, type BoxProps, Text } from '@timelyapp/react';
import React from 'react';
import { useSelector } from 'react-redux';
import { getClientEntities } from '~redux/selectors/clients';
import type { UseIntegrationModalManager } from '../../hooks/useIntegrationModalManager';

export interface IntegrationsWidgetsUsefulInfoProps extends Omit<BoxProps, 'children'> {
  configManager: UseIntegrationModalManager;
  title?: string;
  children?: React.ReactNode;
}

/**
 * Widget that displays context-appropriate useful information
 * based on the integration type, scope, and current configuration.
 */
export const IntegrationsWidgetsUsefulInfo: React.FC<IntegrationsWidgetsUsefulInfoProps> = ({
  configManager,
  title = 'Good to know',
  ...rest
}) => {
  const { active, integrationDetails } = configManager;
  const integrationName = integrationDetails?.title || 'integration';
  const connection = active.connection;
  const settings = connection?.settings || {};
  const hasFallbackClient = !!settings.fallback_timely_client_id;
  const isPersonalScope = active.scope === 'personal';
  const isAccountScope = active.scope === 'account';

  // Get client entities for lookup
  const clientEntities = useSelector(getClientEntities);
  const fallbackClient = hasFallbackClient
    ? clientEntities[settings.fallback_timely_client_id!]
    : null;

  // Build context-appropriate useful info items
  const infoItems = [];

  // Account scope specific items - ordered as requested
  if (isAccountScope) {
    // 1. Client matching information - FIRST (fallback_client_id sensitive)
    if (hasFallbackClient && fallbackClient) {
      infoItems.push({
        icon: <Briefcase02 width={16} height={16} />,
        title: 'Client matching',
        description: `Projects without a clear match will use ${fallbackClient.name} as the fallback client`,
      });
    } else {
      infoItems.push({
        icon: <Briefcase02 width={16} height={16} />,
        title: 'Client matching',
        description:
          'Projects will be assigned to clients when possible. You can set a fallback client in settings',
      });
    }

    // 3. Data security - keep last
    infoItems.push({
      icon: '🔒',
      title: 'Data is secure',
      description: `Timely won't mirror any destructive actions from ${integrationName}`,
    });
  }

  // Personal scope - shown during onboarding to explain identity verification
  if (isPersonalScope) {
    // Identity verification info for personal connections
    infoItems.push({
      icon: <Users02 width={16} height={16} />,
      title: 'Identity verification',
      description: `This verifies who you are to ${integrationName} and gives access to workspace imported data`,
    });

    // Security info
    infoItems.push({
      icon: '🔒',
      title: 'Your data remains private and secure',
      description: 'Only you have access to your personal space data',
    });
  }

  return (
    <Box.Column {...rest} gap="large">
      {!!title && <Text.Headline size="xsmall">{title}</Text.Headline>}
      <Box.Column gap="large">
        {infoItems.map((item, index) => (
          <Box.Row key={index} alignItems="flex-start" gap="medium">
            <Box.Row alignItems="center" marginTop={2}>
              {typeof item.icon === 'string' ? <Text>{item.icon}</Text> : item.icon}
            </Box.Row>
            <Box.Column>
              <Text size="small" color="normal">
                {item.title}
              </Text>
              <Text size="small" color="subtle">
                {item.description}
              </Text>
            </Box.Column>
          </Box.Row>
        ))}
      </Box.Column>
    </Box.Column>
  );
};
